<div class="container-fluid">
  <div class="card p-0 border-0 shadow-none">
    <div class="card-body p-3">
      <!-- Header -->
      <div class="d-flex align-items-center gap-3 mb-4">
        <button class="btn btn-soft-secondary btn-sm" (click)="goBack()" title="Back to labours">
          <i class="bi bi-arrow-left"></i>
        </button>
        <div>
          <h1 class="card-title mb-0">{{ labourName || 'Advance Ledger' }}</h1>
          <span class="sub-text">Advance history — every amount given, returned and recovered from salary</span>
        </div>
      </div>

      <!-- Date range -->
      <div class="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div>
          <label class="form-label text-muted small mb-1">From</label>
          <input type="date" class="form-control" [(ngModel)]="filters.from_date" />
        </div>
        <div>
          <label class="form-label text-muted small mb-1">To</label>
          <input type="date" class="form-control" [(ngModel)]="filters.to_date" [min]="filters.from_date || null" />
        </div>
        <button
          class="btn btn-primary btn-enjochem"
          [disabled]="isLoading || !!dateRangeError"
          (click)="onApplyFilters()">
          Apply
        </button>
        @if (hasDateFilter) {
          <button class="btn btn-secondary" [disabled]="isLoading" (click)="onClearFilters()">Clear</button>
        }
      </div>

      @if (dateRangeError) {
        <div class="text-danger small mb-3">{{ dateRangeError }}</div>
      }

      @if (isLoading) {
        <app-loading-spinner [message]="'Loading advance ledger...'" />
      } @else if (errorMessage) {
        <app-error-alert [error]="errorMessage"></app-error-alert>
      } @else if (ledger) {
        <!-- Summary -->
        <div class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="info-card p-3 border h-100">
              <div class="text-muted small">Opening Balance</div>
              <div class="fw-semibold">{{ formatDecimalAmount(ledger.opening_balance) }}</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="info-card p-3 border h-100">
              <div class="text-muted small">Total Given (+)</div>
              <div class="fw-semibold text-warning">{{ formatDecimalAmount(ledger.total_given) }}</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="info-card p-3 border h-100">
              <div class="text-muted small">Total Received Back (-)</div>
              <div class="fw-semibold text-success">{{ formatDecimalAmount(ledger.total_received_back) }}</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="info-card p-3 border h-100">
              <div class="text-muted small">Closing Balance</div>
              <app-advance-balance [value]="ledger.closing_balance" />
            </div>
          </div>
        </div>

        <!-- Ledger -->
        <div class="table-responsive border bg-white">
          <table class="table align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Date</th>
                <th class="text-end">Given Advance (+)</th>
                <th class="text-end">Received Back (-)</th>
                <th>Remarks</th>
                <th class="text-end">Running Balance</th>
              </tr>
            </thead>

            <tbody>
              <!-- Without this, the first row's running balance looks like it starts from nowhere -->
              @if (showOpeningBalance) {
                <tr class="table-light">
                  <td colspan="4" class="fw-semibold text-muted">
                    Opening balance
                    @if (filters.from_date) {
                      <span class="fw-normal">
                        (carried forward from before {{ filters.from_date | date: 'dd MMM yyyy' }})
                      </span>
                    }
                  </td>
                  <td class="text-end">
                    <app-advance-balance [value]="ledger.opening_balance" [compact]="true" />
                  </td>
                </tr>
              }

              <!-- Rendered in the order the API returned: the running balance is computed in that order. -->
              @for (entry of ledger.entries; track $index) {
                <tr [class.bg-light]="isSalaryDeduction(entry)">
                  <td class="text-nowrap">
                    <div>{{ entry.date | date: 'dd MMM yyyy' }}</div>
                    <span
                      class="app-badge"
                      [ngClass]="isSalaryDeduction(entry) ? 'badge-secondary' : 'badge-soft-primary'">
                      @if (isSalaryDeduction(entry)) {
                        <i class="bi bi-lock-fill me-1"></i>
                      }
                      {{ entryTypeLabel(entry) }}
                    </span>
                  </td>
                  <td class="text-end">
                    {{ formatDecimalAmount(entry.given) }}
                  </td>
                  <td class="text-end">
                    {{ formatDecimalAmount(entry.received_back) }}
                  </td>
                  <td>
                    <span [class.text-muted]="isSalaryDeduction(entry)">{{ entry.remarks || '—' }}</span>
                    @if (isSalaryDeduction(entry)) {
                      <div class="text-muted" style="font-size: 0.7rem">
                        Generated by a salary run — not editable here.
                      </div>
                    }
                  </td>
                  <td class="text-end">
                    <app-advance-balance [value]="entry.running_balance" [compact]="true" />
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5" class="text-center text-muted py-4">
                    @if (hasDateFilter) {
                      No advance activity in the selected date range.
                    } @else {
                      No advances recorded for this labour yet.
                    }
                  </td>
                </tr>
              }
            </tbody>

            @if (ledger.entries.length) {
              <tfoot class="table-light">
                <tr>
                  <td class="fw-semibold">Totals</td>
                  <td class="text-end fw-semibold">{{ formatDecimalAmount(ledger.total_given) }}</td>
                  <td class="text-end fw-semibold">{{ formatDecimalAmount(ledger.total_received_back) }}</td>
                  <td class="text-muted small">Closing balance &rarr;</td>
                  <td class="text-end">
                    <app-advance-balance [value]="ledger.closing_balance" [compact]="true" />
                  </td>
                </tr>
              </tfoot>
            }
          </table>
        </div>
      }
    </div>
  </div>
</div>
